///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                                  RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { useState } from "react";
import Select from "react-select";
import { filter } from "lodash";

import Spinner from "../icons/Spinner";
import ChevronDown from "../icons/ChevronDown";
import messages from "../../helpers/messages";

const customStyles = ({ error, isMini }) => ({
  control: (provided, { isFocused, isDisabled }) => {
    let borderColor = "#ADAFB2";
    if (error) {
      borderColor = "#790000";
    } else if (isFocused) {
      borderColor = "#69C764";
    } else if (isDisabled) {
      borderColor = "#C9CACC";
    }
    return {
      ...provided,
      border: "1px solid " + borderColor,
      boxShadow: "none",
      minHeight: isMini ? 26 : 32,
      maxHeight: isMini ? 26 : 32,
      backgroundColor: "#FFFFFF",
      "&:hover": {
        borderColor: isFocused ? "69C764" : "#343434"
      }
    };
  },
  option: (provided, { isFocused }) => ({
    ...provided,
    color: "#343434",
    fontSize: 12,
    backgroundColor: isFocused ? "#F5F5F6" : "#FFFFFF",
    padding: "7px 14px"
  }),
  valueContainer: (
    provided,
    { isDisabled, selectProps: { isMulti, isSearchable, value, menuIsOpen } }
  ) => ({
    ...provided,
    fontSize: 12,
    padding: isMulti ? "5px 8px" : "9px 14px",
    color: isDisabled ? "#adafb2" : "#343434",
    textOverflow: "ellipsis",
    maxWidth: "90%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    display: "initial",
    marginTop: isMulti && isSearchable && value && !menuIsOpen ? 3 : undefined
  }),
  menu: style => ({
    ...style,
    marginTop: 2,
    borderRadius: 4,
    boxShadow:
      "0 1px 3px 0 rgba(0,0,0,0.2), 0 2px 1px -1px rgba(0,0,0,0.12), 0 1px 1px 0 rgba(0,0,0,0.14)"
  }),
  menuList: styles => ({
    ...styles,
    maxHeight: 184
  }),
  singleValue: provided => ({
    ...provided,
    top: isMini ? 12 : 16,
    marginLeft: 0,
    float: "left"
  }),
  input: provided => ({
    ...provided,
    padding: 0,
    margin: 0
  }),
  indicatorsContainer: (
    provided,
    { selectProps: { isMulti, isSearchable, value, menuIsOpen } }
  ) => ({
    ...provided,
    paddingRight: 8,
    marginTop: isMulti && isSearchable && value && !menuIsOpen ? -10 : undefined
  }),
  multiValue: (provided, { isDisabled }) => ({
    ...provided,
    backgroundColor: "#ffffff",
    margin: 0,
    color: isDisabled ? "#adafb2" : "#343434"
  }),
  multiValueRemove: provided => ({
    ...provided,
    display: "none"
  }),
  multiValueLabel: () => ({
    marginLeft: 0,
    padding: "0px 0px 0px 4px",
    fontSize: 12
  })
});

export const AutoComplete = ({
  isMulti,
  handleChange,
  validationMessage,
  isDisabled,
  handleOptionsMapping,
  staticOptions,
  fetchFrom,
  error,
  value,
  label,
  children,
  isMini = false,
  filterDynamicOptions = true,
  required = false,
  placeholder = "",
  isCustomOptionsWithLink = false,
  ...rest
}) => {
  const [isLoading, toggleLoading] = useState(false);
  const [shrink, toggleShrink] = useState(false);
  const [options, setOptions] = useState([]);

  const NoOptionsComponent = ({ inputValue }) => {
    const valid = inputValue && inputValue.length > 2;
    return (
      <div
        className={
          "no-option-container" + (valid || isLoading ? "" : " minimum-char")
        }
      >
        {isLoading || (
          <span className="content">
            {valid
              ? messages.noResultsFound.defaultMessage
              : messages.pleaseEnter3OrMoreCharacters.defaultMessage}
          </span>
        )}
        {isLoading && (
          <span className="content">
            <Spinner
              className="spinner"
              height="36px"
              width="36px"
              fill="#C9CACC"
            />
          </span>
        )}
      </div>
    );
  };

  const toggleMultiValueSelection = (selectedValue, isSelected) => {
    if (!isSelected) {
      if (value && value.slice) {
        let selectedValues = value.slice();
        selectedValues.push(selectedValue);
        onChange(selectedValues);
      } else {
        onChange([selectedValue]);
      }
    } else {
      const filteredValues = filter(
        value,
        option => option.value !== selectedValue.value
      );
      onChange(filteredValues.length === 0 ? null : filteredValues);
    }
  };

  const MultiSelectOptionComponent = ({
    data,
    isSelected,
    label,
    isFocused,
    innerRef
  }) => (
    <div
      style={{ backgroundColor: isFocused ? "#f5f5f6" : undefined }}
      className={
        "multi-select-option pointer" + (data.disabled ? " disabled" : "")
      }
      onClick={() => {
        !data.disabled && toggleMultiValueSelection(data, isSelected);
      }}
      ref={innerRef}
    >
      <label className="checkbox-container">
        <input
          defaultChecked={isSelected}
          type="checkbox"
          disabled={data.disabled}
        ></input>
        <span className="checkmark"></span>
      </label>
      <span className="pl-24 check-text">{label}</span>
    </div>
  );

  const CustomOptionWithLinkComponent = props => {
    const { label, isFocused, data } = props;

    return (
      <div
        className={`custom-option ${data.value === -2 ? "pointer pl-20" : ""}`}
        style={{
          backgroundColor: isFocused ? "#f5f5f6" : undefined,
          borderTop: data.value === -2 ? "1px solid #C0C7D0" : ""
        }}
        {...props.innerProps}
        onClick={() => {
          if (data.value === -1) {
            return;
          } else if (data.value === -2) {
            window.open(data.url);
          } else {
            props.innerProps.onClick();
          }
        }}
      >
        <span className={`${data.value === -2 ? "hyperlink" : ""}`}>
          {label}
        </span>
      </div>
    );
  };

  const components = {
    DropdownIndicator: () =>
      !staticOptions ? null : (
        <ChevronDown
          fill="#707174"
          height={7}
          width={12}
          rotateDegree={shrink ? 180 : 0}
          className="pos-relative"
          style={{ top: -1 }}
        />
      ),
    ClearIndicator: () => null,
    IndicatorSeparator: () => null,
    MultiValueContainer: () => ""
  };
  if (isMulti) {
    components.Option = MultiSelectOptionComponent;
  }
  if (isCustomOptionsWithLink) {
    components.Option = CustomOptionWithLinkComponent;
  }

  const handleInputChange = searchTerm => {
    if (!staticOptions && searchTerm && searchTerm.length > 2) {
      toggleLoading(true);
      setOptions([]);
      fetchFrom(searchTerm)
        .then(({ data }) => {
          toggleLoading(false);
          setOptions(handleOptionsMapping(data));
        })
        .catch(
          error => error.message === "Cancelled By User" || toggleLoading(false)
        );
    }
  };

  const onChange = selectedValue => {
    const sortedValues = [];
    if (!staticOptions) {
      !selectedValue && setOptions([]);
      selectedValue && setOptions([selectedValue]);
    }

    if (isMulti && staticOptions && staticOptions.length) {
      if (selectedValue && selectedValue.length) {
        staticOptions.forEach(option => {
          if (
            selectedValue.findIndex(({ value }) => value === option.value) !==
            -1
          ) {
            sortedValues.push(option);
          }
        });
        handleChange && handleChange(sortedValues);
      } else {
        handleChange && handleChange(null);
      }
    } else {
      handleChange && handleChange(selectedValue);
    }
  };

  return (
    <div className={"auto-complete " + (isMini ? "mini" : "")}>
      <Select
        classNamePrefix="auto-complete"
        styles={customStyles({ error, isMulti, isMini })}
        className={shrink || value || placeholder ? "shrink" : ""}
        options={
          !isLoading && !staticOptions && options.length === 0 && value
            ? [value]
            : staticOptions || options
        }
        isClearable
        placeholder={placeholder}
        components={components}
        backspaceRemovesValue={isMulti ? false : true}
        onMenuOpen={() => toggleShrink(true)}
        onMenuClose={() => toggleShrink(false)}
        noOptionsMessage={NoOptionsComponent}
        onInputChange={handleInputChange}
        isDisabled={isDisabled}
        value={value}
        onChange={onChange}
        isSearchable={staticOptions ? false : true}
        isMulti={isMulti}
        hideSelectedOptions={false}
        filterOption={
          fetchFrom && !filterDynamicOptions ? options => options : undefined
        }
        {...rest}
      />
      <label
        className={
          "auto-complete-label " +
          (error && " error ") +
          (isDisabled && " disabled ") +
          (required && " required ")
        }
      >
        {label}
      </label>
      {error && validationMessage && (
        <span className="validation-message">{validationMessage}</span>
      )}
    {isMulti && value && (
        <span className="multi-select-value">
            {(Array.isArray(value) ? value : [value])
                .map(o => typeof o === 'object' && o && 'label' in o
                    ? String(o.label ?? '')
                    : (() => {
                        const opt = Array.isArray(staticOptions) ? staticOptions.find(x => x && x.value === o) : null;
                        return opt ? String(opt.label ?? '') : String(o ?? '');
                    })()
                )
                .join(', ')
            }
        </span>
      )}
      {children}
    </div>
  );
};

export default AutoComplete;
